import { useMemo, useCallback, useState } from 'react';
import { Activity, Gauge, type LucideIcon } from 'lucide-react';
import { Area, AreaChart, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@g17eco/ui-modern/components/ui/card';
import { MetricCard } from '@g17eco/ui-modern/components/ui/metric-card';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@g17eco/ui-modern/components/ui/chart';
import { EmissionsKPICards } from '../EmissionsKPICards';
import { ScopeBreakdownChart } from '../ScopeBreakdownChart';
import { ActivityTable } from '../ActivityTable';
import { AddActivityToExistingAssetButton } from '../form/AddActivityToExistingAssetButton';
import { SourceBreakdownChart } from '../assets/SourceBreakdownChart';
import { CalculationDetailsDrawer } from '../CalculationDetailsDrawer';
import {
  useGetAssetQuery,
  useGetAssetByMonthQuery,
  useGetActivitiesQuery,
  useCreateActivityMutation,
  useTriggerCalculationMutation,
} from '../../api/emissions';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { useEmissionsFormatter } from '../../hooks/useEmissionsFormatter';
import { expandMonthToPeriodRange } from '../../utils/activityUtils';
import { convertEmissionValue } from '../../utils/emissionsFormatter';
import { INTENSITY_FIELD_META } from '../../config/intensityFieldBranding';
import { SCOPE_COLORS } from '../../config/scopeBranding';
import {
  EmissionUnit,
  type ActivityRecord,
  type EmissionAsset,
  type EmissionSummary,
  type AssetComparisonItem,
  type ActivityUnit,
  type FuelType,
  type VehicleType,
  type RefrigerantGasType,
  type ProcessType,
  type CreateActivityRequest,
  type MonthlyReport,
} from '../../types/emissions';
import { ACTIVITY_TYPE_LABELS } from '../../config/activityTypeBranding';
import type { AssetActivityEntry } from '../form/types';

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const monthlyChartConfig: ChartConfig = {
  scope1: { label: 'Scope 1', color: SCOPE_COLORS.scope1 },
  scope2: { label: 'Scope 2', color: SCOPE_COLORS.scope2 },
  scope3: { label: 'Scope 3', color: SCOPE_COLORS.scope3 },
};

interface MonthlyChartRow {
  month: string;
  scope1: number;
  scope2: number;
  scope3: number;
}

/**
 * Build a continuous month-by-month series across [start, end], with one
 * value per scope per bucket. Fills gaps with zeros so missing months
 * render as empty space rather than collapsing the x-axis. Labels use
 * a 2-digit year (e.g. "Mar '25") for compact rendering.
 */
const buildMonthlySeries = (
  data: MonthlyReport[] | undefined,
  start: string,
  end: string,
  toDisplayUnit: (kg: number) => number,
): MonthlyChartRow[] => {
  const grouped: Record<string, { scope1: number; scope2: number; scope3: number }> = {};
  for (const item of data ?? []) {
    const key = `${item.year}-${String(item.month).padStart(2, '0')}`;
    if (!grouped[key]) grouped[key] = { scope1: 0, scope2: 0, scope3: 0 };
    const scopeKey = item.scope as 'scope1' | 'scope2' | 'scope3';
    if (scopeKey === 'scope1' || scopeKey === 'scope2' || scopeKey === 'scope3') {
      grouped[key][scopeKey] += toDisplayUnit(item.total);
    }
  }
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || startDate > endDate) return [];
  const filled: MonthlyChartRow[] = [];
  let y = startDate.getUTCFullYear();
  let m = startDate.getUTCMonth() + 1;
  const endY = endDate.getUTCFullYear();
  const endM = endDate.getUTCMonth() + 1;
  while (y < endY || (y === endY && m <= endM)) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    const bucket = grouped[key];
    filled.push({
      month: `${monthNames[m - 1]} '${String(y).slice(2)}`,
      scope1: bucket?.scope1 ?? 0,
      scope2: bucket?.scope2 ?? 0,
      scope3: bucket?.scope3 ?? 0,
    });
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return filled;
};

interface AssetDetailSectionProps {
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  asset: EmissionAsset;
  comparisonAsset?: AssetComparisonItem;
  /**
   * All-time bounds for the second trend chart. Optional — if omitted (no
   * activity data available yet) the all-time chart is skipped and the
   * fiscal-year trend takes the full row.
   */
  allTimePeriodStart?: string;
  allTimePeriodEnd?: string;
}

export const AssetDetailSection = ({
  initiativeId,
  periodStart,
  periodEnd,
  asset,
  comparisonAsset,
  allTimePeriodStart,
  allTimePeriodEnd,
}: AssetDetailSectionProps) => {
  const { formatCompact, settings } = useEmissionsFormatter();
  const [activityPage, setActivityPage] = useState(0);
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [selectedActivity, setSelectedActivity] = useState<ActivityRecord | null>(null);
  const PAGE_SIZE = 10;

  const [triggerCalculation, { isLoading: isRecalculating }] = useTriggerCalculationMutation();

  // Use the asset's own initiativeId — it may belong to a child initiative
  const assetInitiativeId = asset.initiativeId || initiativeId;

  // Fetch full asset detail (list endpoint may omit fields like employeeCount, customIntensity)
  const { data: fullAsset } = useGetAssetQuery(
    { initiativeId: assetInitiativeId, assetId: asset._id },
    { skip: !asset._id },
  );
  const assetData = fullAsset ?? asset;

  const { data: monthlyData } = useGetAssetByMonthQuery(
    { initiativeId: assetInitiativeId, assetId: asset._id, periodStart, periodEnd },
    { skip: !asset._id },
  );

  // All-time series — only fetched when a wider window is provided. Reused
  // by the second trend card to give multi-year context next to the
  // fiscal-year chart.
  const hasAllTime = Boolean(allTimePeriodStart && allTimePeriodEnd);
  const { data: allTimeMonthlyData } = useGetAssetByMonthQuery(
    {
      initiativeId: assetInitiativeId,
      assetId: asset._id,
      periodStart: allTimePeriodStart ?? periodStart,
      periodEnd: allTimePeriodEnd ?? periodEnd,
    },
    { skip: !asset._id || !hasAllTime },
  );

  const { data: activitiesData, isLoading: isLoadingActivities } = useGetActivitiesQuery(
    {
      initiativeId: assetInitiativeId,
      assetId: asset._id,
      periodStart,
      periodEnd,
      limit: PAGE_SIZE,
      offset: activityPage * PAGE_SIZE,
      sort: 'periodStart',
      sortDir: 'desc',
      ...(sourceFilter ? { activityType: sourceFilter } : {}),
    },
    { skip: !asset._id },
  );

  // Source-filter options derive from the asset's `comparisonAsset.activityTypes`
  // (backend tells us exactly which activity types this asset has data for in
  // the selected period — so the dropdown only ever lists relevant choices).
  const sourceOptions = useMemo(() => {
    const types = comparisonAsset?.activityTypes ?? [];
    return [...types]
      .sort((a, b) => b.co2e - a.co2e)
      .map((t) => ({
        value: t.activityType,
        label: ACTIVITY_TYPE_LABELS[t.activityType] ?? t.activityType.replace(/_/g, ' '),
      }));
  }, [comparisonAsset?.activityTypes]);

  const handleSourceFilterChange = (next: string) => {
    setSourceFilter(next);
    setActivityPage(0);
  };

  const handleViewActivity = (activity: ActivityRecord) => {
    setSelectedActivity(activity);
  };

  const handleRecalculate = async (activityId: string, activityInitiativeId: string) => {
    try {
      await triggerCalculation({
        initiativeId: activityInitiativeId,
        activityId,
        options: { recalculate: true },
      }).unwrap();
      generateToast({ color: 'success', message: 'Emissions recalculated successfully' });
    } catch (error) {
      toastFromError({ error, title: 'Recalculation failed' });
    }
  };

  const [createActivity] = useCreateActivityMutation();
  const activities = activitiesData?.data ?? [];
  const totalActivities = activitiesData?.pagination?.total ?? 0;

  const totalCo2e = comparisonAsset?.totalCo2e ?? 0;

  const handlePersistActivity = useCallback(
    async (entry: AssetActivityEntry): Promise<{ activityId: string }> => {
      if (!assetInitiativeId) {
        throw new Error('initiativeId is required');
      }
      const { periodStart: ps, periodEnd: pe } = expandMonthToPeriodRange(entry.periodStart, entry.periodEnd);
      const data: CreateActivityRequest = {
        scope: entry.scope,
        activityType: entry.activityType,
        periodStart: ps,
        periodEnd: pe,
        quantity: Number(entry.quantity),
        unit: entry.unit as ActivityUnit,
        region: entry.region,
        regionFallback: entry.regionFallback,
        yearFallback: entry.yearFallback,
        assetId: asset._id,
        ...(entry.fuelType ? { fuelType: entry.fuelType as FuelType } : {}),
        ...(entry.vehicleType ? { vehicleType: entry.vehicleType as VehicleType } : {}),
        ...(entry.refrigerantType ? { refrigerantType: entry.refrigerantType as RefrigerantGasType } : {}),
        ...(entry.processType ? { processType: entry.processType as ProcessType } : {}),
        ...(entry.name ? { name: entry.name.trim() } : {}),
        ...(entry.dataSource ? { dataSource: entry.dataSource.trim() } : {}),
        ...(entry.notes ? { notes: entry.notes.trim() } : {}),
      };
      const result = await createActivity({ initiativeId: assetInitiativeId, data }).unwrap();
      return { activityId: result.data._id };
    },
    [assetInitiativeId, asset._id, createActivity],
  );

  // Build EmissionSummary for reusing KPI Cards + Scope Breakdown
  const assetSummary: EmissionSummary | undefined = useMemo(() => {
    if (!comparisonAsset) return undefined;
    const byScope: Record<string, number> = {};
    let actCount = 0;
    for (const s of comparisonAsset.scopes) {
      byScope[s.scope] = s.co2e;
      actCount += s.activityCount;
    }
    return {
      totalCO2e: comparisonAsset.totalCo2e,
      unit: EmissionUnit.KgCO2e,
      totalTonnesCO2e: comparisonAsset.totalCo2e / 1000,
      byScope,
      activityCount: actCount,
    };
  }, [comparisonAsset]);

  // Monthly trend data — span the full selected period so gaps render as empty bars.
  const toDisplayUnit = useCallback(
    (kg: number) => convertEmissionValue(kg, settings.displayUnit),
    [settings.displayUnit],
  );

  const monthlyChartData = useMemo(
    () => buildMonthlySeries(monthlyData, periodStart, periodEnd, toDisplayUnit),
    [monthlyData, periodStart, periodEnd, toDisplayUnit],
  );

  const allTimeChartData = useMemo(() => {
    if (!hasAllTime) return [];
    return buildMonthlySeries(
      allTimeMonthlyData,
      allTimePeriodStart ?? periodStart,
      allTimePeriodEnd ?? periodEnd,
      toDisplayUnit,
    );
  }, [hasAllTime, allTimeMonthlyData, allTimePeriodStart, allTimePeriodEnd, periodStart, periodEnd, toDisplayUnit]);

  // Intensity tiles — one per populated denominator field on the asset, plus
  // the user-defined custom intensity. Rendered in their own labelled row.
  // Each tile shows the ratio (e.g. "155.2 kgCO₂e/m²") and a muted "icon —
  // label — value" denominator chip beneath it (e.g. ruler icon · Floor Area · 3,833 m²).
  const assetRecord = assetData as unknown as Record<string, unknown>;
  const intensityCards: Array<{
    label: string;
    value: number;
    unit: string;
    borderColor: string;
    denomIcon: LucideIcon;
    denomIconColor: string;
    denomLabel: string;
    denomValue: string;
  }> = [];

  for (const [key, meta] of Object.entries(INTENSITY_FIELD_META)) {
    const raw = assetRecord[key];
    if (typeof raw !== 'number') continue;
    const threshold = meta.minValue ?? 0;
    if (raw <= threshold) continue;
    intensityCards.push({
      label: `Per ${meta.label}`,
      value: totalCo2e / raw,
      unit: meta.unit,
      borderColor: meta.borderColor,
      denomIcon: meta.icon,
      denomIconColor: meta.iconColor,
      denomLabel: meta.label,
      denomValue: meta.formatValue(raw),
    });
  }

  const customLabel = assetData.customIntensityLabel;
  const customValue = assetData.customIntensityValue;
  if (customLabel && typeof customValue === 'number' && customValue > 0) {
    intensityCards.push({
      label: `Per ${customLabel}`,
      value: totalCo2e / customValue,
      unit: `kgCO₂e/${customLabel}`,
      borderColor: 'tw:border-l-emerald-500',
      denomIcon: Gauge,
      denomIconColor: 'tw:text-emerald-500',
      denomLabel: customLabel,
      denomValue: customValue.toLocaleString(),
    });
  }

  return (
    <div className='tw:space-y-6'>
      {/* KPI strip — total + scope breakdown, no outer card. */}
      <EmissionsKPICards summary={assetSummary} isLoading={false} />

      {/* Intensity metrics — labelled row of MetricCards. Hidden when the asset has no denominators. */}
      {intensityCards.length > 0 && (
        <div className='tw:space-y-3'>
          <div className='tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-semibold tw:text-muted-foreground'>
            <Gauge className='tw:size-4' />
            Intensity Metrics
          </div>
          <div className='tw:grid tw:grid-cols-2 tw:md:grid-cols-3 tw:lg:grid-cols-4 tw:gap-4'>
            {intensityCards.map((ic, i) => (
              <MetricCard
                key={i}
                label={ic.label}
                value={ic.value.toFixed(1)}
                unit={ic.unit}
                subtextNode={
                  <div className='tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:text-muted-foreground'>
                    <ic.denomIcon className={`tw:size-3.5 tw:shrink-0 ${ic.denomIconColor}`} />
                    <span>{ic.denomLabel}</span>
                    <span className='tw:font-medium tw:text-foreground'>{ic.denomValue}</span>
                  </div>
                }
                icon={<i className='fa fa-gauge tw:text-muted-foreground' />}
                iconBg='tw:bg-muted'
                borderColor={ic.borderColor}
              />
            ))}
          </div>
        </div>
      )}

      {/* Trends — fiscal-year and all-time side by side. The all-time card is
          hidden when no wider window is available (single-year footprint). */}
      <div className={`tw:grid tw:grid-cols-1 tw:gap-4 ${hasAllTime ? 'tw:lg:grid-cols-2' : ''}`}>
        <TrendCard
          title='Emissions Trend'
          description={`Monthly emissions (${formatCompact(0).unit}) — selected period`}
          data={monthlyChartData}
          variant='bar'
        />
        {hasAllTime && (
          <TrendCard
            title='All-Time Trend'
            description={`Monthly emissions (${formatCompact(0).unit}) across all years`}
            data={allTimeChartData}
            variant='area'
          />
        )}
      </div>

      {/* Scope donut + Source donut — 50/50 on a separate row. */}
      <div className='tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-4'>
        <ScopeBreakdownChart initiativeId={initiativeId} summary={assetSummary} />
        <SourceBreakdownChart activityTypes={comparisonAsset?.activityTypes ?? []} totalCo2e={totalCo2e} />
      </div>

      {/* Linked Activities — separate card. */}
      <Card>
        <CardHeader>
          <div className='tw:flex tw:items-center tw:justify-between tw:gap-3 tw:flex-wrap'>
            <CardTitle className='tw:flex tw:items-center tw:gap-2'>
              <Activity className='tw:size-4 tw:text-muted-foreground' />
              Linked Activities ({totalActivities})
            </CardTitle>
            <div className='tw:flex tw:items-center tw:gap-2'>
              {sourceOptions.length > 1 && (
                <select
                  value={sourceFilter}
                  onChange={(e) => handleSourceFilterChange(e.target.value)}
                  className='tw:h-9 tw:rounded-md tw:border tw:border-input tw:bg-transparent tw:px-3 tw:text-sm tw:text-foreground'
                  aria-label='Filter activities by source'
                >
                  <option value=''>All sources</option>
                  {sourceOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
              <AddActivityToExistingAssetButton
                asset={assetData}
                onPersistActivity={handlePersistActivity}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ActivityTable
            activities={activities}
            isLoading={isLoadingActivities}
            queryPeriod={{ start: periodStart, end: periodEnd }}
            onViewCalculation={handleViewActivity}
          />
          {totalActivities > PAGE_SIZE && (
            <div className='tw:flex tw:items-center tw:justify-between tw:mt-4 tw:text-sm tw:text-muted-foreground'>
              <span>
                {activityPage * PAGE_SIZE + 1}&ndash;{Math.min((activityPage + 1) * PAGE_SIZE, totalActivities)} of{' '}
                {totalActivities}
              </span>
              <div className='tw:flex tw:gap-2'>
                <button
                  onClick={() => setActivityPage(activityPage - 1)}
                  disabled={activityPage === 0}
                  className='tw:px-2 tw:py-1 tw:border tw:rounded tw:text-xs tw:disabled:opacity-50'
                >
                  Prev
                </button>
                <button
                  onClick={() => setActivityPage(activityPage + 1)}
                  disabled={(activityPage + 1) * PAGE_SIZE >= totalActivities}
                  className='tw:px-2 tw:py-1 tw:border tw:rounded tw:text-xs tw:disabled:opacity-50'
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CalculationDetailsDrawer
        isOpen={selectedActivity !== null}
        toggle={() => setSelectedActivity(null)}
        initiativeId={selectedActivity?.initiativeId ?? assetInitiativeId}
        activityId={selectedActivity?._id ?? null}
        onRecalculate={handleRecalculate}
        isRecalculating={isRecalculating}
        activityData={selectedActivity ? {
          activityType: selectedActivity.activityType,
          bulkImportId: selectedActivity.bulkImportId,
          quantity: selectedActivity.quantity,
          unit: selectedActivity.unit,
          region: selectedActivity.region,
          periodStart: selectedActivity.periodStart,
          periodEnd: selectedActivity.periodEnd,
          scope: selectedActivity.scope,
          fuelType: selectedActivity.fuelType,
          vehicleType: selectedActivity.vehicleType,
          refrigerantType: selectedActivity.refrigerantType,
          processType: selectedActivity.processType,
          regionFallback: selectedActivity.regionFallback,
          yearFallback: selectedActivity.yearFallback,
          calculationNotices: selectedActivity.calculationNotices,
        } : undefined}
      />
    </div>
  );
};

interface TrendCardProps {
  title: string;
  description: string;
  data: MonthlyChartRow[];
  /** "bar" = stacked bars (selected period), "area" = stacked areas (all-time). */
  variant: 'bar' | 'area';
}

const TrendCard = ({ title, description, data, variant }: TrendCardProps) => {
  const hasData = data.some((d) => d.scope1 + d.scope2 + d.scope3 > 0);
  return (
    <Card className='tw:h-full'>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className='tw:flex tw:items-center tw:justify-center tw:h-[280px] tw:text-muted-foreground'>
            <p className='tw:text-sm'>No monthly data available</p>
          </div>
        ) : (
          <ChartContainer config={monthlyChartConfig} className='tw:h-[280px] tw:w-full'>
            {variant === 'bar' ? (
              <BarChart data={data}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey='month' tickLine={false} axisLine={false} tickMargin={8} tick={{ fontSize: 10 }} />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(v: number) => v.toLocaleString()}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <ChartLegend content={<ChartLegendContent />} />
                <Bar dataKey='scope1' stackId='scopes' fill='var(--color-scope1)' />
                <Bar dataKey='scope2' stackId='scopes' fill='var(--color-scope2)' />
                <Bar dataKey='scope3' stackId='scopes' fill='var(--color-scope3)' radius={[4, 4, 0, 0]} />
              </BarChart>
            ) : (
              <AreaChart data={data}>
                <defs>
                  <linearGradient id='area-scope1' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor={SCOPE_COLORS.scope1} stopOpacity={0.6} />
                    <stop offset='95%' stopColor={SCOPE_COLORS.scope1} stopOpacity={0.1} />
                  </linearGradient>
                  <linearGradient id='area-scope2' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor={SCOPE_COLORS.scope2} stopOpacity={0.6} />
                    <stop offset='95%' stopColor={SCOPE_COLORS.scope2} stopOpacity={0.1} />
                  </linearGradient>
                  <linearGradient id='area-scope3' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor={SCOPE_COLORS.scope3} stopOpacity={0.6} />
                    <stop offset='95%' stopColor={SCOPE_COLORS.scope3} stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} />
                <XAxis dataKey='month' tickLine={false} axisLine={false} tickMargin={8} tick={{ fontSize: 10 }} />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(v: number) => v.toLocaleString()}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <ChartLegend content={<ChartLegendContent />} />
                <Area
                  type='monotone'
                  dataKey='scope1'
                  stackId='scopes'
                  stroke={SCOPE_COLORS.scope1}
                  fill='url(#area-scope1)'
                />
                <Area
                  type='monotone'
                  dataKey='scope2'
                  stackId='scopes'
                  stroke={SCOPE_COLORS.scope2}
                  fill='url(#area-scope2)'
                />
                <Area
                  type='monotone'
                  dataKey='scope3'
                  stackId='scopes'
                  stroke={SCOPE_COLORS.scope3}
                  fill='url(#area-scope3)'
                />
              </AreaChart>
            )}
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};
